import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Users, MessageCircle, Radio } from "lucide-react";
import { GoalCard } from "@/components/dashboard/GoalCard";
import { MovementCard } from "@/components/dashboard/MovementCard";
import { useInbox } from "@/components/inbox/InboxProvider";
import { isLate } from "@/components/inbox/inboxUtils";
import { DayMonitor } from "@/components/ops/DayMonitor";
import { LiveFeed } from "@/components/ops/LiveFeed";
import { SmsBalanceCard } from "@/components/dashboard/SmsBalanceCard";
import { AppStatsCard } from "@/components/dashboard/AppStatsCard";
import { PanelHealthCard } from "@/components/dashboard/PanelHealthCard";
import { ServiceDueCard } from "@/components/dashboard/ServiceDueCard";
import { BiaCard } from "@/components/dashboard/BiaCard";
import { DueTodayList } from "@/components/dashboard/DueTodayList";
import { TrialButton } from "@/components/TrialDialog";
import { WeekChart } from "@/components/dashboard/WeekChart";
import { RecoverCard } from "@/components/dashboard/RecoverCard";
import { RecoverDialog } from "@/components/ops/RecoverDialog";

const Stat = ({ testid, icon: Icon, label, value, sub, color }) => (
  <div data-testid={testid} className="rounded-2xl border border-[#1F293D] bg-[#111827] p-5 fade-up">
    <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-slate-400">
      {label}<span className="rounded-lg p-1.5" style={{ background: `${color}1A` }}><Icon className="h-4 w-4" style={{ color }} /></span>
    </div>
    <div className="mt-3 font-mono text-3xl font-extrabold text-white">{value}</div>
    {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
  </div>
);

export const OpsDashboard = () => {
  const [d, setD] = useState(null);
  const [rec, setRec] = useState(null);
  const inbox = useInbox();
  const load = useCallback(() => api.get("/dashboard/ops").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  if (!d) return <div data-testid="ops-loading" className="py-20 text-center text-slate-500">Carregando...</div>;
  const late = inbox.items.filter(isLate).length;
  return (
    <div data-testid="ops-dashboard" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl">Operação do dia</h1>
          <p className="mt-2 text-sm text-slate-400">Vencimentos, faturas enviadas e monitoramento de envios · atualiza a cada 20 s</p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <TrialButton />
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <Radio className="h-4 w-4 animate-pulse text-emerald-400" /> SMS {d.sms_auto ? "ligado" : "desligado"} · WhatsApp {d.wa_ready ? "ligado" : "aguardando aprovação"}
        </div>
        </div>
      </div>
      <GoalCard goal={d.goal} onSaved={load} ops />
      <MovementCard />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat testid="ops-active" icon={Users} label="Clientes ativos" value={d.active.toLocaleString("pt-BR")} sub="todos os painéis" color="#34D399" />
        <Stat testid="ops-due-yesterday" icon={Users} label="Venceram ontem" value={d.days.ontem.total} sub={`ainda não renovaram · ${d.days.ontem.missing} sem fatura entregue`} color="#F87171" />
        <Stat testid="ops-due-today" icon={Users} label="Vencem hoje" value={d.days.hoje.total} sub={`ainda não renovaram · ${d.days.hoje.missing} sem fatura entregue`} color="#FBBF24" />
        <Link to="/respostas" data-testid="ops-responses" className={`rounded-2xl border p-5 transition-colors fade-up ${late ? "border-red-500/50 bg-red-500/10" : "border-[#1F293D] bg-[#111827] hover:border-emerald-500/40"}`}>
          <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-slate-400">Respostas do Financeiro<MessageCircle className="h-4 w-4 text-[#25D366]" /></div>
          <div className="mt-3 font-mono text-3xl font-extrabold text-white">{inbox.open}</div>
          <div className={`mt-1 text-xs ${late ? "font-semibold text-red-300" : "text-slate-500"}`}>{late ? `${late} aguardando há +30 min` : "pendentes de atendimento"}</div>
        </Link>
      </div>
      <ServiceDueCard />
      <PanelHealthCard />
      <SmsBalanceCard balance={d.sms_balance} onRefresh={load} />
      <AppStatsCard />
      <BiaCard />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <WeekChart days={d.week} counts />
        <RecoverCard recover={{ d7: { count: d.recover.d7 }, d30: { count: d.recover.d30 }, older: { count: d.recover.older } }} counts onOpen={(group, label) => setRec({ group, label })} />
      </div>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        {[["ontem", "Ontem"], ["hoje", "Hoje"], ["amanha", "Amanhã"]].map(([k, l]) => <DayMonitor key={k} id={k} label={l} day={d.days[k]} onSent={load} />)}
      </div>
      {rec && <RecoverDialog group={rec.group} label={rec.label} onClose={() => { setRec(null); load(); }} />}
      <DueTodayList items={d.due_today} />
      <LiveFeed />
    </div>
  );
};
